import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { schemas } from '../types/zod-api-schema';
import { firstTimeLogin } from '../services/authService';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../contexts/AuthProvider';
import { config } from '../config/app.config';

export default function LoginPage() {
    const [user_name, setuser_name] = useState('');
    const [updated_user_name, setUpdatedUserName] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<{ user_name?: string; password?: string }>({});
    const location = useLocation();
    const toast = useToast();
    const { login } = useAuth();
    const [isFirstTimeLogin, setIsFirstTimeLogin] = useState(false);
    const [isPasswordReset, setIsPasswordReset] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [firstLoginData, setFirstLoginData] = useState<{ user_id: number; user_name: string; token: string } | null>(null);

    useEffect(() => {
        const fullSearch = location.search;
        const params = new URLSearchParams(fullSearch);
        const userId = params.get('user_id');
        const userName = params.get('user_name');
        const token = params.get('token');
        const reset = params.get('reset') === '1';

        if (userId && userName && token) {
            setIsFirstTimeLogin(true);
            setIsPasswordReset(reset);
            setFirstLoginData({
                user_id: parseInt(userId),
                user_name: userName,
                token: token
            });
            setuser_name(userName);
            // A password reset keeps the existing username - no rename step.
            if (reset) setUpdatedUserName(userName);
        }
    }, [location.search]);

    const handleNormalSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFieldErrors({});
        setError('');

        const result = schemas.LoginRequest.safeParse({ user_name, password });
        if (!result.success) {
            const errors: { user_name?: string; password?: string } = {};
            result.error.issues.forEach((issue: any) => {
                if (issue.path[0] === 'user_name') errors.user_name = issue.message;
                if (issue.path[0] === 'password') errors.password = issue.message;
            });
            setFieldErrors(errors);
            return;
        }

        try {
            setLoading(true);
            await login(user_name, password);
        } catch (err: any) {
            const errorMsg = err.message || 'Login failed';
            setError(errorMsg);
            toast.showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleFirstTimeLoginSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFieldErrors({});
        setError('');

        if (!password || (!isPasswordReset && !updated_user_name)) {
            const errors: { user_name?: string; password?: string } = {};
            if (!password) errors.password = 'Password is required';
            if (!isPasswordReset && !updated_user_name) errors.user_name = 'New username is required';
            setFieldErrors(errors);
            return;
        }

        try {
            setLoading(true);
            if (firstLoginData) {
                await firstTimeLogin(
                    firstLoginData.user_id,
                    firstLoginData.user_name,
                    updated_user_name,
                    password,
                    firstLoginData.token
                );
                toast.showSuccess('First-time login successful! Redirecting...');
                await login(updated_user_name, password);
            }
        } catch (err: any) {
            setError(err.message || 'First-time login failed');
            toast.showError(err.message || 'First-time login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = isFirstTimeLogin ? handleFirstTimeLoginSubmit : handleNormalSubmit;

    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: '#f8fafc',
            backgroundImage: 'linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            <style>
                {`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    
                    @keyframes float {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-10px); }
                    }
                    
                    @keyframes shimmer {
                        0% { background-position: -200% 0; }
                        100% { background-position: 200% 0; }
                    }
                    
                    .login-container {
                        animation: fadeIn 0.6s ease-out;
                    }
                    
                    .logo-float {
                        animation: float 3s ease-in-out infinite;
                    }
                    
                    .shimmer-button {
                        background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #6366f1 100%);
                        background-size: 200% 100%;
                        animation: shimmer 2s infinite linear;
                    }
                    
                    /* Tablet styles */
                    @media (max-width: 768px) {
                        .logo-float {
                            width: 60px !important;
                            height: 60px !important;
                            animation: none;
                        }
                        
                        .login-container {
                            max-width: 90% !important;
                            padding: 16px !important;
                        }
                        
                        .login-card {
                            padding: 32px 24px !important;
                            border-radius: 20px !important;
                        }
                        
                        .login-heading {
                            font-size: 24px !important;
                        }
                        
                        .login-subheading {
                            font-size: 13px !important;
                        }
                        
                        .decorative-bg {
                            display: none;
                        }
                    }
                    
                    /* Mobile styles */
                    @media (max-width: 480px) {
                        .logo-float {
                            width: 50px !important;
                            height: 50px !important;
                            margin-bottom: 16px !important;
                        }
                        
                        .login-container {
                            max-width: 100% !important;
                            padding: 12px !important;
                        }
                        
                        .login-card {
                            padding: 24px 20px !important;
                            border-radius: 16px !important;
                        }
                        
                        .login-heading {
                            font-size: 22px !important;
                            margin-bottom: 6px !important;
                        }
                        
                        .login-subheading {
                            font-size: 12px !important;
                        }
                        
                        .form-input {
                            padding: 14px 16px !important;
                            font-size: 14px !important;
                        }
                        
                        .form-button {
                            padding: 16px !important;
                            font-size: 15px !important;
                        }
                        
                        .form-label {
                            font-size: 13px !important;
                            margin-bottom: 8px !important;
                        }
                        
                        .corner-accent {
                            display: none;
                        }
                    }
                `}
            </style>

            {/* Background decorative elements */}
            <div className="decorative-bg" style={{
                position: 'fixed',
                top: '10%',
                left: '10%',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #c7d2fe 0%, #ddd6fe 100%)',
                opacity: 0.1,
                filter: 'blur(40px)',
                zIndex: 0
            }} />
            <div className="decorative-bg" style={{
                position: 'fixed',
                bottom: '10%',
                right: '10%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ddd6fe 0%, #c7d2fe 100%)',
                opacity: 0.1,
                filter: 'blur(40px)',
                zIndex: 0
            }} />

            <div className="login-container" style={{
                width: '100%',
                maxWidth: '420px',
                padding: '20px',
                position: 'relative',
                zIndex: 1
            }}>
                <div style={{
                    position: 'relative',
                    width: '100%'
                }}>
                    {loading && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(248, 250, 252, 0.9)',
                            borderRadius: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10
                        }}>
                            <Loading message={isFirstTimeLogin ? 'Setting up your account. Please wait…' : 'Signing you in. Please wait…'} />
                        </div>
                    )}

                    {/* Header with logo */}
                    <div style={{
                        textAlign: 'center',
                        marginBottom: '32px',
                        position: 'relative'
                    }}>
                        <div className="logo-float" style={{
                            width: '70px',
                            height: '70px',
                            margin: '0 auto 16px',
                            borderRadius: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                        }}>
                            <img
                                src="/aib-icon.png"
                                alt="AIB Bank Logo"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <h2 className="login-heading" style={{
                            fontSize: '26px',
                            fontWeight: '700',
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            marginBottom: '8px'
                        }}>
                            {isPasswordReset ? 'Reset Password' : isFirstTimeLogin ? 'Complete Setup' : 'Welcome Back'}
                        </h2>
                        <p className="login-subheading" style={{
                            color: '#64748b',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}>
                            {isPasswordReset
                                ? 'Choose a new password for your account'
                                : isFirstTimeLogin
                                    ? 'Set up your account credentials'
                                    : 'Sign in to continue to your account'}
                        </p>
                    </div>

                    {!!error && (
                        <div style={{
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '16px',
                            padding: '20px',
                            marginBottom: '28px',
                            color: '#dc2626',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '12px',
                            animation: 'fadeIn 0.3s ease-out'
                        }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} style={{ width: '100%' }} autoComplete={import.meta.env.PROD ? "off" : undefined}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {isFirstTimeLogin ? (
                                <>
                                    {!isPasswordReset && (
                                    <>
                                    <div>
                                        <input
                                            className="form-input"
                                            type="text"
                                            value={user_name}
                                            disabled
                                            placeholder="Temporary Username"
                                            onChange={() => { }}
                                            style={{
                                                width: '100%',
                                                height: '50px',
                                                padding: '0 18px',
                                                border: '2px solid #e2e8f0',
                                                borderRadius: '24px',
                                                fontSize: '14px',
                                                lineHeight: '50px',
                                                textAlign: 'center',
                                                backgroundColor: '#f8fafc',
                                                color: '#64748b',
                                                transition: 'all 0.2s ease',
                                                outline: 'none',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <input
                                            type="text"
                                            value={updated_user_name}
                                            onChange={(e) => {
                                                setUpdatedUserName(e.target.value);
                                                setError('');
                                            }}
                                            required
                                            autoFocus
                                            disabled={loading}
                                            placeholder="Choose your username"
                                            className="form-input"
                                            style={{
                                                width: '100%',
                                                height: '50px',
                                                padding: '0 18px',
                                                border: fieldErrors.user_name ? '2px solid #dc2626' : '2px solid #e2e8f0',
                                                borderRadius: '24px',
                                                fontSize: '14px',
                                                lineHeight: '50px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                transition: 'all 0.2s ease',
                                                outline: 'none',
                                                boxShadow: fieldErrors.user_name ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : 'none',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#6366f1';
                                                e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                if (!fieldErrors.user_name) {
                                                    e.target.style.borderColor = '#e2e8f0';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        />
                                        {fieldErrors.user_name && (
                                            <div style={{
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                marginTop: '8px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                                {fieldErrors.user_name}
                                            </div>
                                        )}
                                    </div>
                                    </>
                                    )}
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setError('');
                                            }}
                                            required
                                            disabled={loading}
                                            placeholder="Create a strong password"
                                            className="form-input"
                                            style={{
                                                width: '100%',
                                                height: '50px',
                                                padding: '0 48px 0 18px',
                                                border: fieldErrors.password ? '2px solid #dc2626' : '2px solid #e2e8f0',
                                                borderRadius: '24px',
                                                fontSize: '14px',
                                                lineHeight: '50px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                transition: 'all 0.2s ease',
                                                outline: 'none',
                                                boxSizing: 'border-box',
                                                boxShadow: fieldErrors.password ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#6366f1';
                                                e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                if (!fieldErrors.password) {
                                                    e.target.style.borderColor = '#e2e8f0';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{
                                                position: 'absolute',
                                                right: '16px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            {showPassword ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                                    <line x1="1" y1="1" x2="23" y2="23" />
                                                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                                                </svg>
                                            )}
                                        </button>
                                        {fieldErrors.password && (
                                            <div style={{
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                marginTop: '8px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                                {fieldErrors.password}
                                            </div>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <input
                                            type="text"
                                            value={user_name}
                                            onChange={(e) => {
                                                setuser_name(e.target.value);
                                                setError('');
                                            }}
                                            required
                                            autoFocus
                                            disabled={loading}
                                            placeholder="Enter your username"
                                            className="form-input"
                                            style={{
                                                width: '100%',
                                                height: '50px',
                                                padding: '0 18px',
                                                border: fieldErrors.user_name ? '2px solid #dc2626' : '2px solid #e2e8f0',
                                                borderRadius: '24px',
                                                fontSize: '14px',
                                                lineHeight: '50px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                transition: 'all 0.2s ease',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                boxShadow: fieldErrors.user_name ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#6366f1';
                                                e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                if (!fieldErrors.user_name) {
                                                    e.target.style.borderColor = '#e2e8f0';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        />
                                        {fieldErrors.user_name && (
                                            <div style={{
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                marginTop: '8px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                                {fieldErrors.user_name}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setError('');
                                            }}
                                            required
                                            disabled={loading}
                                            placeholder="Enter your password"
                                            className="form-input"
                                            style={{
                                                width: '100%',
                                                height: '50px',
                                                padding: '0 48px 0 18px',
                                                border: fieldErrors.password ? '2px solid #dc2626' : '2px solid #e2e8f0',
                                                borderRadius: '24px',
                                                fontSize: '14px',
                                                lineHeight: '50px',
                                                textAlign: 'center',
                                                backgroundColor: 'white',
                                                boxSizing: 'border-box',
                                                transition: 'all 0.2s ease',
                                                outline: 'none',
                                                boxShadow: fieldErrors.password ? '0 0 0 4px rgba(220, 38, 38, 0.1)' : 'none'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#6366f1';
                                                e.target.style.boxShadow = '0 0 0 4px rgba(99, 102, 241, 0.1)';
                                            }}
                                            onBlur={(e) => {
                                                if (!fieldErrors.password) {
                                                    e.target.style.borderColor = '#e2e8f0';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{
                                                position: 'absolute',
                                                right: '16px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#94a3b8'
                                            }}
                                        >
                                            {showPassword ? (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                                    <circle cx="12" cy="12" r="3" />
                                                </svg>
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                                    <line x1="1" y1="1" x2="23" y2="23" />
                                                    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                                                </svg>
                                            )}
                                        </button>
                                        {fieldErrors.password && (
                                            <div style={{
                                                color: '#dc2626',
                                                fontSize: '13px',
                                                marginTop: '8px',
                                                marginLeft: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <line x1="12" y1="8" x2="12" y2="12" />
                                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                                </svg>
                                                {fieldErrors.password}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className={!loading ? "shimmer-button form-button" : "form-button"}
                                style={{
                                    width: '100%',
                                    padding: '18px',
                                    backgroundColor: loading ? '#94a3b8' : '#6366f1',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '24px',
                                    fontSize: '15px',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    marginTop: '8px',
                                    boxSizing: 'border-box',
                                    transition: 'all 0.2s ease',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 10px 30px rgba(99, 102, 241, 0.4)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!loading) {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }
                                }}
                            >
                                {loading ? (
                                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="white" strokeOpacity="0.3" strokeWidth="4" />
                                            <path d="M12 2a10 10 0 0110 10" stroke="white" strokeWidth="4" strokeLinecap="round" />
                                        </svg>
                                        Please wait...
                                    </span>
                                ) : (
                                    isPasswordReset ? 'Set New Password' : isFirstTimeLogin ? 'Complete Setup' : 'Sign In'
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Footer note */}
                    {!isFirstTimeLogin && (
                        <div style={{
                            marginTop: '28px',
                            paddingTop: '20px',
                            borderTop: '1px solid #f1f5f9',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                color: '#64748b',
                                fontSize: '12px',
                                fontWeight: '500'
                            }}>
                                {
                                    config.creditUnionName
                                        ? `© ${new Date().getFullYear()} ${config.creditUnionName}. All rights reserved.`
                                        : `© ${new Date().getFullYear()} Rajida POS. All rights reserved.`
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}